"use client";

import { useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Winner, WinnerCategories } from "../types";
import { buildCategoryHref } from "../category-utils";
import { getWinnerCardImageProps } from "../winners-image-priority";
import WinnerCard from "./WinnerCard";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface CategorySectionProps {
  category: WinnerCategories;
  categoryIndex: number;
  winners: Winner[];
}

const MAX_WINNERS_IN_SWIPER = 12;

export default function CategorySection({ category, categoryIndex, winners }: CategorySectionProps) {
  const [prevBtn, setPrevBtn] = useState<HTMLButtonElement | null>(null);
  const [nextBtn, setNextBtn] = useState<HTMLButtonElement | null>(null);

  const visibleWinners = winners.filter(w => w.SuppressResults !== "True").slice(0, MAX_WINNERS_IN_SWIPER);

  if (visibleWinners.length === 0) {
    return null;
  }

  const href = buildCategoryHref(category);

  return (
    <section className="flex flex-col gap-4 lg:gap-8 px-3 max-lg:overflow-hidden">
      <div className="flex items-center justify-between gap-4">
        <h2 className="!mb-0 text-left normal-case text-dark-1 font-jost text-xl lg:text-[22px] font-semibold leading-[26px] lg:leading-7">
          {category.Value}
        </h2>

        <a
          href={href}
          data-testid={`category-section-view-all-${category.Key}`}
          className="inline-flex items-center gap-1 shrink-0 font-jost text-[13px] lg:text-base font-medium leading-[18px] lg:leading-[22px] text-primary hover:text-[#FF6A00] transition-colors">
          <span className="lg:underline lg:underline-offset-2">View all</span>
          <svg
            className="shrink-0 size-4"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <path
              d="m6 4 4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>

      <div className="relative">
        <Swiper
          modules={[Navigation, Pagination]}
          slidesPerView={1.15}
          spaceBetween={8}
          pagination={{
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 1,
          }}
          navigation={{
            prevEl: prevBtn,
            nextEl: nextBtn,
          }}
          breakpoints={{
            640: { slidesPerView: 2.1, spaceBetween: 8 },
            1024: { slidesPerView: 3.1, spaceBetween: 16 },
          }}
          className="w-full !pb-8 max-lg:!overflow-visible">
          {visibleWinners.map(winner => (
            <SwiperSlide key={`${category.Key}-${winner.CompetitionRef || winner.Url}`} className="h-auto">
              <div className="h-full">
                <WinnerCard winner={winner} {...getWinnerCardImageProps(categoryIndex === 0)} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          ref={setPrevBtn}
          type="button"
          aria-label={`Previous ${category.Value} winners`}
          className="hidden lg:flex absolute left-0 top-[calc(50%-1rem)] z-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center size-11 text-dark-1 hover:text-primary bg-white rounded-xl shadow-[0_1.5px_8px_0_rgba(0,0,0,0.2)] transition-colors [&.swiper-button-disabled]:opacity-40 [&.swiper-button-disabled]:pointer-events-none">
          <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m15 6-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          ref={setNextBtn}
          type="button"
          aria-label={`Next ${category.Value} winners`}
          className="hidden lg:flex absolute right-0 top-[calc(50%-1rem)] z-10 translate-x-1/2 -translate-y-1/2 items-center justify-center size-11 text-dark-1 hover:text-primary bg-white rounded-xl shadow-[0_1.5px_8px_0_rgba(0,0,0,0.2)] transition-colors [&.swiper-button-disabled]:opacity-40 [&.swiper-button-disabled]:pointer-events-none">
          <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </section>
  );
}
